import { useNavigate } from 'react-router-dom'

import {
  F0Dialog,
  F0Form,
  f0FormField,
  useF0Form,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import {
  GivePerformanceFeedbackDocument,
  PerformanceFeedbacksGivenDataCollectionDocument,
  FeedbacksFeedbackEntryVisibilityEnum,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { multiEmployeeSelectorV2FormField } from 'modules/employees/components/EmployeeSelectorV2/formField'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { useNavigation } from 'modules/core/hooks/useNavigation'

const VISIBILITY_OPTIONS = [
  'private',
  'private_and_manager',
  'manager_only',
] as const satisfies readonly FeedbacksFeedbackEntryVisibilityEnum[]

interface GiveFeedbackDialogProps {
  onClose: () => void
}

export function GiveFeedbackDialog({ onClose }: GiveFeedbackDialogProps) {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [sendFeedbackMutation] = useMutation(GivePerformanceFeedbackDocument, {
    extractorFn: (data) => data.feedbacksMutations.createFeedbacksFeedbackEntry,
    refetchQueries: [PerformanceFeedbacksGivenDataCollectionDocument],
  })

  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  const schema = z.object({
    receiverEmployeeSelection: multiEmployeeSelectorV2FormField({
      mandatory: true,
      label: i18n.t('performance_feedbacks.pages.give_feedback.who_to_give'),
      section: '',
      fieldConfig: {
        forcedFilters: { excludeCurrentEmployee: true },
        options: { disableSelectAll: true },
      },
    }),
    visibility: f0FormField(z.enum(VISIBILITY_OPTIONS), {
      label: i18n.t('performance_feedbacks.pages.give_feedback.who_should_see'),
      fieldType: 'select',
      options: [
        {
          label: i18n.t('performance_feedbacks.pages.give_feedback.labels.receiver_only'),
          value: 'private',
        },
        {
          label: i18n.t('performance_feedbacks.pages.give_feedback.labels.receiver_and_manager'),
          value: 'private_and_manager',
        },
        {
          label: i18n.t('performance_feedbacks.pages.give_feedback.labels.manager_only'),
          value: 'manager_only',
        },
      ],
    }),
    feedback: f0FormField(z.string().min(1), {
      label: i18n.t('performance_feedbacks.pages.give_feedback.write_feedback'),
      placeholder: i18n.t('performance_feedbacks.pages.give_feedback.placeholder'),
      fieldType: 'textarea',
    }),
  })

  const formDefinition = useF0FormDefinition({
    name: 'give-feedback',
    schema,
    defaultValues: {
      receiverEmployeeSelection: { type: 'manual' as const, ids: [] },
      visibility: 'private' as const,
      feedback: '',
    },
    onSubmit: async ({ data }) => {
      const response = await sendFeedbackMutation({
        variables: {
          feedback: data.feedback,
          receiverEmployeeSelection: data.receiverEmployeeSelection,
          visibility: data.visibility,
        },
      })

      const result = handleResult(response, () => null)
      if (!result.success) return { success: false as const, errors: {} }

      app.setFlash({
        type: 'correct',
        message: i18n.t('performance_process.overview.participation.send_reminders.success'),
      })

      track(trackingEvents.performanceFeedbacks.sendFeedback)
      navigate(navigation.performanceFeedbacks.given.fullPath())
      onClose()

      return { success: true as const }
    },
    submitConfig: { type: 'default', hideSubmitButton: true },
  })

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('performance_feedbacks.pages.give_feedback.title')}
      primaryAction={{
        label: i18n.t('performance_feedbacks.pages.give_feedback.send'),
        onClick: submit,
        loading: isSubmitting,
        disabled: isSubmitting || hasErrors,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <F0Form formRef={formRef} formDefinition={formDefinition} />
    </F0Dialog>
  )
}
